/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { Forecast } from '@futrli-sdk/predict';
import { QUERY_PARAMS, ROUTES } from '@futrli/shared-services';

import * as styles from '../onboarding-nav.scss';
import { ForecastType } from '../shared/onboarding-type-select';
import { SCENARIO_ONBOARDING_STEP } from '../const';
import { getDateMinusOneDay } from '../../../utils/dates';
import { ScenarioDatePicker } from '../../detail/predictions/predictions-nav-data-controls/scenario-date-picker';
import { PeriodsMenu } from '../../detail/predictions/predictions-nav-data-controls/periods-menu';
import { NewAssumptionButton } from '../../detail/predictions/components/new-assumption-button';
import { CashFlowSwitch } from '../../detail/predictions/predictions-nav-data-controls/cash-flow-switch';
import { ForecastingMenu } from '../../detail/predictions/predictions-nav-data-controls/forecasts-menu';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';

export const ScenarioOnboardingNav = () => {
  const { onboardingStep } = useParams();
  const { forecast, isConsolidation, updateForecast } = usePredictionsInfoContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const forecastType = searchParams.get(QUERY_PARAMS.FORECAST_TYPE);

  let scenarioSteps;
  switch (forecastType) {
    case ForecastType.FromLYA:
      scenarioSteps = 10;
      break;
    case ForecastType.WithAutoPredictions:
      scenarioSteps = 11;
      break;
    case ForecastType.FromScratch:
      scenarioSteps = 9;
      break;
    default:
      scenarioSteps = 9;
      break;
  }

  const initialWidth = 100 / scenarioSteps;
  const onboardingStepNum = onboardingStep ? Number(onboardingStep) : 0;
  const currentWidth = (onboardingStepNum + 1) * initialWidth;

  const showDateAndPeriods =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_WHAT_YOU_SEE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_WHAT_YOU_SEE)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.LYA_CONTROL_WHAT_YOU_SEE));

  const showNewAssumption =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.SCRATCH_CUSTOMISE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE)) ||
    (forecastType === ForecastType.FromLYA && onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.LYA_CUSTOMISE));

  const showCashFlowSwitchAccordingToSteps =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW));

  const hideCashFlowSwitchAccordingToPath =
    location.pathname.indexOf(ROUTES.CASH_FLOW_STATEMENT) !== -1 ||
    location.pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;

  const showCashFlowSwitch = showCashFlowSwitchAccordingToSteps && !hideCashFlowSwitchAccordingToPath;

  const showForecastControlsMenu =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.SCRATCH_ADDITIONAL_CONTROLS)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_ADDITIONAL_CONTROLS)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_ONBOARDING_STEP.LYA_ADDITIONAL_CONTROLS));

  const newAssumptionButtonIsOpen =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum === Number(SCENARIO_ONBOARDING_STEP.SCRATCH_CUSTOMISE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum === Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE)) ||
    (forecastType === ForecastType.FromLYA && onboardingStepNum === Number(SCENARIO_ONBOARDING_STEP.LYA_CUSTOMISE));

  const hideCashflowPlaceholder = hideCashFlowSwitchAccordingToPath;

  return (
    <>
      <nav className={styles.navContainer}>
        {showForecastControlsMenu ? (
          <ForecastingMenu />
        ) : (
          <div className={styles.forecastControlsPlaceholder} style={{ marginRight: 30 }} />
        )}
        <div className={styles.divider} />
        {showCashFlowSwitch ? (
          <>
            <div style={{ height: 34, marginRight: 18 }} />
            <CashFlowSwitch />
          </>
        ) : hideCashflowPlaceholder ? null : (
          <div className={styles.cashFlowPlaceholder} style={{ marginLeft: 30 }} />
        )}
        {showDateAndPeriods && forecast ? (
          <>
            <ScenarioDatePicker
              forecastFromDate={forecast?.forecast_from_date}
              includeUnpaidInvoices={isConsolidation ? false : (forecast as Forecast).include_unpaid_invoices}
              updateForecast={updateForecast}
              windowFrom={forecast.window_from}
              windowTill={getDateMinusOneDay(forecast.window_till)}
            />
            <PeriodsMenu />
          </>
        ) : (
          <>
            <div className={styles.datePickerPlaceholder} style={{ marginLeft: 'auto' }} />
            <div className={styles.periodsPlaceholder} style={{ marginLeft: 23 }} />
          </>
        )}

        {showNewAssumption ? (
          <NewAssumptionButton isOpenInitially={newAssumptionButtonIsOpen} />
        ) : (
          <div className={styles.newPredictionPlaceholder} style={{ marginLeft: 13 }} />
        )}
        <div style={{ width: 20 }} />
        <div className={styles.providerPlaceholder} />
      </nav>
      <div className={styles.progressiveBarContainer}>
        <div className={classNames(styles.progressBar)} style={{ width: `${currentWidth}%` }} />
      </div>
    </>
  );
};
